import React from "react";
import PropTypes from "prop-types";
import { ProgressBar } from "react-bootstrap";
import "../../../../assets/scss/_marker-scale.scss";

/**
 * MarkerProgressBar component
 * This component renders a customizable progress bar with optional boundary indicators.
 * It visually represents the progress with different styles based on the provided props.
 * Supports both standard markers and multi-segment scales like Vitamin B12.
 *
 * @returns {JSX.Element} The rendered progress bar component.
 */

const MarkerProgressBar = ({
  showLowBoundary,
  leftBarSize,
  barSize,
  showHighBoundary,
  value,
  code,
  scaleSegments
}) => {
  const isVitaminB12 = code === "B12A" && scaleSegments;

  // Determine the status class for the right bar
  const getRightBarClass = () => {
    if (value === "Unconfirmed Reactive") return "markerScale__bar--unconfirmed";
    return "markerScale__bar--bad";
  };

  if (isVitaminB12) {
    // Render B12-specific multi-segment progress bar
    return (
      <ProgressBar className="mt-0 markerScale__bar-container">
        {showLowBoundary && (
          <ProgressBar
            className="mr-0 markerScale__bar markerScale__bar--low"
            now={scaleSegments.lowSize}
          />
        )}
        <ProgressBar 
          className="mx-0 markerScale__bar--lownormal" 
          now={scaleSegments.lowNormalSize} 
        />
        <ProgressBar 
          className="mx-0 markerScale__bar--normal" 
          now={scaleSegments.normalSize} 
        />
        {showHighBoundary && (
          <ProgressBar
            className="ml-0 markerScale__bar markerScale__bar--high"
            now={scaleSegments.highSize}
          />
        )}
      </ProgressBar>
    );
  }

  // Render standard progress bar for non-B12 markers
  return (
    <ProgressBar className="mt-0 markerScale__bar-container">
      {showLowBoundary && (
        <ProgressBar
          className="mr-0 markerScale__bar markerScale__bar--low"
          now={leftBarSize || barSize}
        />
      )}
      <ProgressBar className="mx-0 markerScale__bar" now={barSize} />
      {showHighBoundary && (
        <ProgressBar
          className={`ml-0 markerScale__bar ${getRightBarClass()}`}
          now={barSize}
        />
      )}
    </ProgressBar>
  );
};

MarkerProgressBar.propTypes = {
  showLowBoundary: PropTypes.bool,
  leftBarSize: PropTypes.number,
  barSize: PropTypes.number.isRequired,
  showHighBoundary: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  code: PropTypes.string,
  scaleSegments: PropTypes.shape({
    lowSize: PropTypes.number,
    lowNormalSize: PropTypes.number,
    normalSize: PropTypes.number,
    highSize: PropTypes.number,
    lowBoundary: PropTypes.number,
    lowNormalBoundary: PropTypes.number,
    normalBoundary: PropTypes.number,
    pointerPosition: PropTypes.number
  })
};

MarkerProgressBar.defaultProps = {
  showLowBoundary: true,
  showHighBoundary: true,
  leftBarSize: 0
};

export default MarkerProgressBar;